import { TFunction } from 'i18next';

/**
 * AWS Cognito error types for password change operations
 */
export type CognitoPasswordChangeErrorType =
  | 'oldPasswordWrong'
  | 'newPasswordWeak'
  | 'accountNotEligible'
  | 'rateLimited'
  | 'invalidSession'
  | 'internalError'
  | 'unknown';

export interface CognitoPasswordChangeError {
  type: CognitoPasswordChangeErrorType;
  userMessage: string;
}

/**
 * Error code mappings for different Cognito error categories
 */
const ERROR_CODE_MAPPINGS: Record<string, CognitoPasswordChangeErrorType> = {
  // Old password incorrect
  InvalidParameterException: 'oldPasswordWrong',
  NotAuthorizedException: 'oldPasswordWrong',

  // New password doesn't meet requirements
  InvalidPasswordException: 'newPasswordWeak',
  PasswordHistoryPolicyViolationException: 'newPasswordWeak',

  // Account not eligible for password change
  PasswordResetRequiredException: 'accountNotEligible',
  UserNotConfirmedException: 'accountNotEligible',

  // Too many requests / throttling
  LimitExceededException: 'rateLimited',
  TooManyRequestsException: 'rateLimited',

  // Session or account invalid
  UserNotFoundException: 'invalidSession',
  ResourceNotFoundException: 'invalidSession',

  // Internal / AWS-level issue
  ForbiddenException: 'internalError',
  InternalErrorException: 'internalError',
};

/**
 * Translation keys for each error type
 */
const ERROR_MESSAGE_KEYS: Record<CognitoPasswordChangeErrorType, string> = {
  oldPasswordWrong: 'authenticated.settings:passwordChangeErrors.oldPasswordWrong',
  newPasswordWeak: 'authenticated.settings:passwordChangeErrors.newPasswordWeak',
  accountNotEligible: 'authenticated.settings:passwordChangeErrors.accountNotEligible',
  rateLimited: 'authenticated.settings:passwordChangeErrors.rateLimited',
  invalidSession: 'authenticated.settings:passwordChangeErrors.invalidSession',
  internalError: 'authenticated.settings:passwordChangeErrors.internalError',
  unknown: 'authenticated.settings:passwordChangeErrors.unknown',
};

/**
 * Extracts error code from various Cognito error formats
 * Handles both Amplify and raw Cognito SDK error formats
 */
function extractErrorCode(err: unknown): string | null {
  if (!err || typeof err !== 'object') {
    return null;
  }

  const error = err as Record<string, unknown>;

  // Try __type first (raw Cognito SDK format)
  if (typeof error.__type === 'string') {
    return error.__type;
  }

  // Try code (common in Amplify errors)
  if (typeof error.code === 'string') {
    return error.code;
  }

  // Try name (JavaScript Error.name property)
  if (typeof error.name === 'string') {
    return error.name;
  }

  return null;
}

/**
 * Maps AWS Cognito password change errors to user-friendly categories
 *
 * @param err - The error object from Auth.changePassword()
 * @param t - Translation function from useTranslation
 * @returns Object with error type and user-friendly message
 *
 * @example
 * ```ts
 * const { t } = useTranslation();
 * try {
 *   await Auth.changePassword(user, oldPassword, newPassword);
 * } catch (error) {
 *   const { type, userMessage } = mapCognitoChangePasswordError(error, t);
 *   if (type === 'oldPasswordWrong') {
 *     // Handle incorrect password
 *   }
 *   displayToastError(userMessage);
 * }
 * ```
 */
export function mapCognitoChangePasswordError(
  err: unknown,
  t: TFunction
): CognitoPasswordChangeError {
  // Handle non-object errors
  if (!err || typeof err !== 'object') {
    return {
      type: 'unknown',
      userMessage: t(ERROR_MESSAGE_KEYS.unknown),
    };
  }

  const errorCode = extractErrorCode(err);

  // If we couldn't extract an error code, return unknown
  if (!errorCode) {
    return {
      type: 'unknown',
      userMessage: t(ERROR_MESSAGE_KEYS.unknown),
    };
  }

  // Look up the error type from our mappings
  const errorType = ERROR_CODE_MAPPINGS[errorCode] || 'unknown';

  return {
    type: errorType,
    userMessage: t(ERROR_MESSAGE_KEYS[errorType]),
  };
}
